/**
 * Two queues of things the machine thinks, waiting to be agreed with.
 *
 * Both exist for the same reason: the machinery could already measure and
 * compare, and there was no way to work through what it found. `edits/auto`
 * answered one photograph at a time, so the photographs that most need it —
 * faded scans, colour casts, blown scanner borders — never got it, because
 * they look bad in the grid and nobody opens them. And merging two entries
 * that are one person needed you to recognise them yourself, one at a time,
 * which for forty entries is a job nobody does.
 *
 * Neither queue applies anything on its own. `auto` runs at the editor's wider
 * neutralise threshold, which is safe *because* somebody confirms each one —
 * a pass that wrote straight through would eventually turn a warm-lit colour
 * portrait grey. And a wrongly merged person cannot be unmerged.
 */

import { h, replace } from "../dom";
import { api, blobUrl, cropUrl, renditionUrl } from "../api";
import { store } from "../state";
import { empty, failure, loading, toast } from "../ui";
import { count, personName } from "../format";
import type { MergeSuggestion, Proposal } from "../types";
import type { View } from "./view";

export function mount(pane: HTMLElement): View {
  let generation = 0;
  let tab: "edits" | "people" = "edits";

  // --- edits somebody has to agree with ------------------------------------

  function proposalEl(p: Proposal, again: () => void): HTMLElement {
    const before = blobUrl(p.proxyKey ?? p.contentHash);
    let edits: Record<string, unknown> = {};
    try {
      edits = JSON.parse(p.edits) as Record<string, unknown>;
    } catch {
      /* a proposal we cannot read is one we cannot describe */
    }

    /**
     * What it proposes, in words.
     *
     * A row of numbers is not a decision anybody can make. "Brighter, and the
     * cast taken off" is.
     */
    const words: string[] = [];
    const n = (k: string): number => Number(edits[k] ?? 0);
    if (n("brightness") > 0.02) words.push("brighter");
    if (n("brightness") < -0.02) words.push("darker");
    if (n("contrast") > 0.02) words.push("more contrast");
    if (n("contrast") < -0.02) words.push("less contrast");
    if (Math.abs(n("saturation")) > 0.02) {
      words.push(n("saturation") > 0 ? "more colour" : "less colour");
    }
    if (n("neutralize") > 0.02) words.push("the age cast taken off");
    if (n("sharpen") > 0.02) words.push("sharpened");
    if (edits.crop) words.push("the scanner border trimmed");
    if (n("rotate")) words.push("turned upright");
    const lens = edits.lens as { vignette?: number; distortion?: number } | null | undefined;
    if (lens && Object.values(lens).some((v) => Math.abs(Number(v ?? 0)) > 0.02)) {
      words.push("the corners lifted");
    }

    const shot = h("img", {
      class: "revshot",
      src: before ?? "",
      alt: p.fileName,
      loading: "lazy",
    });

    return h(
      "div",
      { class: "revitem" },
      shot,
      h(
        "div",
        { class: "revmeta" },
        h("div", { class: "nm", text: p.fileName }),
        h("div", {
          class: "why",
          text: words.length ? words.join(", ") : "small adjustments",
        }),
        h(
          "div",
          { class: "revacts" },
          h("button", {
            class: "btn sm primary",
            text: "Do it",
            onclick: () => {
              void api
                .decideProposal(store.libraryId, p.assetId, true)
                .then(() => {
                  // The rendered result, so the change is visible rather than
                  // taken on trust.
                  shot.src = renditionUrl(store.libraryId, p.assetId, "edited");
                  toast("Applied. The original is kept — Reset undoes it.");
                  again();
                })
                .catch((e: unknown) =>
                  toast(e instanceof Error ? e.message : "could not apply it", true),
                );
            },
          }),
          h("button", {
            class: "btn sm ghost",
            text: "Leave it",
            onclick: () => {
              void api
                .decideProposal(store.libraryId, p.assetId, false)
                .then(again)
                .catch((e: unknown) =>
                  toast(e instanceof Error ? e.message : "could not save that", true),
                );
            },
          }),
        ),
      ),
    );
  }

  // --- people who might be one person --------------------------------------

  function suggestionEl(s: MergeSuggestion, again: () => void): HTMLElement {
    const face = (side: MergeSuggestion["a"]): HTMLElement =>
      h(
        "div",
        { class: "revface" },
        side.cropKey
          ? h("img", { src: cropUrl(side.cropKey) ?? "", alt: "", loading: "lazy" })
          : h("div", { class: "revface-none", text: "?" }),
        h("div", { class: "nm", text: personName(side.name) }),
        h("div", { class: "why", text: count(side.count, "photograph") }),
      );

    // Whichever has more photographs survives, so the merge moves the smaller
    // group into the larger one and the archive keeps the better-established
    // entry. Named beats unnamed regardless: a name is somebody's work.
    const keepA = s.a.name ? true : s.b.name ? false : s.a.count >= s.b.count;
    const into = keepA ? s.a : s.b;
    const from = keepA ? s.b : s.a;

    return h(
      "div",
      { class: "revpair" },
      h("div", { class: "revfaces" }, face(s.a), h("span", { class: "revvs", text: "or" }), face(s.b)),
      h("div", {
        class: "why",
        text: `${Math.round(s.similarity * 100)}% alike`,
      }),
      h(
        "div",
        { class: "revacts" },
        h("button", {
          class: "btn sm primary",
          text: `Same — keep ${personName(into.name)}`,
          title: `${personName(from.name)}'s photographs move across, and that entry goes`,
          onclick: () => {
            void (async () => {
              try {
                await api.mergeIdentities(store.libraryId, into.identityId, [from.identityId]);
                await api.decideSuggestion(
                  store.libraryId,
                  s.a.identityId,
                  s.b.identityId,
                  "merged",
                );
                toast(`Merged into ${personName(into.name)}.`);
                again();
              } catch (e) {
                toast(e instanceof Error ? e.message : "could not merge them", true);
              }
            })();
          },
        }),
        h("button", {
          class: "btn sm ghost",
          text: "Different people",
          onclick: () => {
            void api
              .decideSuggestion(store.libraryId, s.a.identityId, s.b.identityId, "rejected")
              .then(() => {
                toast("Noted. This pair will not come back.");
                again();
              })
              .catch((e: unknown) =>
                toast(e instanceof Error ? e.message : "could not save that", true),
              );
          },
        }),
      ),
    );
  }

  // --- the screen -----------------------------------------------------------

  function tabs(): HTMLElement {
    const bar = h("div", { class: "modes revtabs" });
    for (const [key, label] of [
      ["edits", "Adjustments"],
      ["people", "Same person?"],
    ] as Array<["edits" | "people", string]>) {
      bar.appendChild(
        h("button", {
          class: tab === key ? "on" : "",
          text: label,
          onclick: () => {
            tab = key;
            void refresh();
          },
        }),
      );
    }
    return bar;
  }

  async function refresh(): Promise<void> {
    const mine = ++generation;
    replace(pane, tabs(), loading(tab === "edits" ? "adjustments" : "people"));

    if (tab === "edits") {
      let found: { pending: number; items: Proposal[] };
      try {
        found = await api.proposals(store.libraryId);
      } catch (e) {
        if (mine !== generation) return;
        replace(pane, tabs(), failure(e, () => void refresh()));
        return;
      }
      if (mine !== generation) return;

      const runBtn = h("button", {
        class: "btn primary",
        text: "Look at everything",
        title: "Measure every photograph and propose what each one needs",
        onclick: () => {
          void api
            .runProposals(store.libraryId)
            .then((r) =>
              toast(
                r.queued
                  ? `Measuring ${count(r.queued, "photograph")}. Come back shortly.`
                  : "Every photograph has already been looked at.",
              ),
            )
            .catch((e: unknown) =>
              toast(e instanceof Error ? e.message : "could not start that", true),
            );
        },
      });

      if (!found.items.length) {
        replace(
          pane,
          tabs(),
          empty({
            icon: "eye",
            title: "Nothing waiting",
            body: [
              "This is where adjustments the archive thinks are needed wait to be " +
                "agreed with — a faded scan brightened, a colour cast taken off, a " +
                "scanner border trimmed.",
              "Nothing is ever applied without being shown here first.",
            ],
            actions: [runBtn],
          }),
        );
        return;
      }

      const head = h(
        "div",
        { class: "gridhead" },
        h("h2", { text: "Adjustments" }),
        h("p", {
          class: "sub",
          text:
            `${count(found.pending, "photograph")} the archive thinks it can improve. ` +
            "Nothing has been changed — the original is always kept, whichever you choose.",
        }),
      );
      const list = h("div", { class: "revlist" });
      for (const item of found.items) list.appendChild(proposalEl(item, () => void refresh()));
      replace(pane, tabs(), head, list);
      return;
    }

    // --- the same-person queue --------------------------------------------
    let found: { items: MergeSuggestion[] };
    try {
      found = await api.suggestions(store.libraryId);
    } catch (e) {
      if (mine !== generation) return;
      replace(pane, tabs(), failure(e, () => void refresh()));
      return;
    }
    if (mine !== generation) return;

    const note = h("p", { class: "sub" });
    const lookBtn = h("button", {
      class: "btn primary",
      text: "Look for pairs",
      title: "Compare everybody with a face against everybody else",
      onclick: () => {
        void (async () => {
          note.textContent = "loading the model…";
          try {
            const { findSimilarPeople } = await import("../similar");
            const r = await findSimilarPeople((done, total) => {
              note.textContent = `comparing ${done} of ${total}`;
            });
            toast(
              r.suggested
                ? `Found ${count(r.suggested, "pair")} to look at.`
                : r.compared < 2
                  ? "Nobody has a face yet, so there is nothing to compare."
                  : "Nobody looks like anybody else.",
            );
            void refresh();
          } catch (e) {
            toast(e instanceof Error ? e.message : "could not compare them", true);
            note.textContent = "";
          }
        })();
      },
    });

    if (!found.items.length) {
      replace(
        pane,
        tabs(),
        empty({
          icon: "people",
          title: "Nothing to ask about",
          body: [
            "The archive makes a new person whenever it meets a face it cannot " +
              "place, so the same grandmother can end up here three times — once " +
              "from the prints, once from the slides, once from a phone.",
            "This finds those pairs so you do not have to recognise them yourself, " +
              "one at a time. The comparison happens in this browser; no face " +
              "measurement ever leaves it.",
          ],
          actions: [lookBtn],
        }),
        note,
      );
      return;
    }

    const head = h(
      "div",
      { class: "gridhead" },
      h("h2", { text: "Same person?" }),
      h("p", {
        class: "sub",
        text: `${count(found.items.length, "pair")} that look alike, most alike first.`,
      }),
      // In a row of its own, or the button stretches to the width of the
      // column — `.gridhead` is a stacked flex and a lone child in it fills.
      h("div", { class: "revheadacts" }, lookBtn, note),
    );
    const list = h("div", { class: "revlist" });
    for (const item of found.items) list.appendChild(suggestionEl(item, () => void refresh()));
    replace(pane, tabs(), head, list);
  }

  void refresh();
  return { refresh: () => void refresh(), destroy() { generation++; } };
}
